import * as React from 'react';
import { useLocale, useTranslations } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type { JobListing } from 'job-bank-data-access';
import { assetBaseUrl } from '../asset-base-url';
import { initialsFor, formatSalary, formatPostedDate } from '../listing-format';
import './JobListingCard.css';

export interface JobListingCardProps {
  listing: JobListing;
  onDismiss?: (id: string) => void;
  onSelect?: (listing: JobListing) => void;
  detailHref?: string;
}

export function JobListingCard({ listing, onDismiss, onSelect, detailHref = '#' }: JobListingCardProps) {
  const locale = useLocale();
  const { t } = useTranslations(assetBaseUrl, locale);

  const title = locale === 'fr' ? listing.title.fr : listing.title.en;
  const initials = initialsFor(listing.employer);
  const salary = `${formatSalary(listing.salaryMin, listing.salaryMax, locale)} ${t(
    'jobListingCard.salaryPerYearSuffix',
  )}`;
  const posted = formatPostedDate(listing.postedDaysAgo, t, 'jobListingCard');

  const titleId = `job-${listing.id}-title`;

  const handleSelect = (e: React.MouseEvent) => {
    if (onSelect) {
      e.preventDefault();
      onSelect(listing);
    }
  };

  return (
    <article className="job-listing-card" aria-labelledby={titleId}>
      <div className="job-listing-card__avatar" aria-hidden="true">
        {initials}
      </div>

      <div className="job-listing-card__body">
        <h3 id={titleId} className="job-listing-card__title">
          <a href={detailHref} className="job-listing-card__title-link" onClick={handleSelect}>
            {title}
          </a>
        </h3>
        <p className="job-listing-card__employer">{listing.employer}</p>
        <p className="job-listing-card__meta">
          {listing.city}, {listing.province} &middot; {listing.workArrangement}
        </p>
        <p className="job-listing-card__salary">{salary}</p>
        <p className="job-listing-card__posted">{posted}</p>
      </div>

      <div className="job-listing-card__actions">
        <a
          id={`job-${listing.id}-chevron`}
          href={detailHref}
          className="job-listing-card__chevron"
          aria-label={t('jobListingCard.viewDetails', { title })}
          onClick={handleSelect}
        >
          <span aria-hidden="true">&rsaquo;</span>
        </a>
        <button
          type="button"
          className="job-listing-card__dismiss"
          onClick={() => onDismiss?.(listing.id)}
          aria-label={t('jobListingCard.dismiss', { title })}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </article>
  );
}
